import { defineStore } from "pinia"
import { computed, ref } from "vue"
import type { AuthCredential } from "@/utils/types"
import { useProviderStore } from "./provider";
import { ubiquAxios } from "@/main";

type SortKey = keyof AuthCredential | 'count.asset_count' | 'count.derived_asset_count' | 'count.ppk_count';

export const useCredentialStore = defineStore("credential", () => {
  const allCredentials = ref<AuthCredential[]>([]);
  const displayedCredentials = ref<AuthCredential[]>([]);
  const sortKey = ref<keyof AuthCredential>("created");
  const sortOrder = ref<"asc" | "desc">();
  const currentPage = ref(1);
  const itemsPerPage = ref(10);
  const authCredentialUUIDs = ref<string[]>([]);
  const providerStore = useProviderStore();

  const sortConfig = ref({
    key: 'created' as SortKey,
    order: 'asc' as 'asc' | 'desc'
  })

  const currentProvider = computed(() => providerStore.currentProvider)
  const totalCredentials = computed(() => allCredentials.value.length)

  const setCredentialUUIDs = (uuids: string[]) => {
    authCredentialUUIDs.value = uuids;
  };

  const fetchAllCredentials = async (providerUuid?: string) => {
    try {
      const response = await ubiquAxios.get('/auth_credential/', {
        params: {
          rse_provider_id: providerUuid || currentProvider.value?.uuid
        }
      });

      const credentials = response.data;
      const credentialUuids = credentials.map((cred: AuthCredential) => cred.uuid);

      if (credentialUuids.length === 0) return;

      const countResponse = await ubiquAxios.post('/rse/count_for_auth_credentials/', {
        auth_credential_ids: credentialUuids,
        types: ["asset", "derived_asset", "ppk"]
      });

      const countMap = new Map();
      countResponse.data.forEach((item: any) => {
        countMap.set(item.uuid, item.count);
      });

      const mergedCredentials = credentials.map((credential: AuthCredential) => {
        return {
          ...credential,
          count: countMap.get(credential.uuid)
        };
      });

      allCredentials.value = mergedCredentials;
      updateDisplayedCredentials();
      } catch (error) {
        console.error("Error fetching credentials: ", error);
        allCredentials.value = [];
        displayedCredentials.value = [];
        throw error;
      }
    };

  const sortCredentials = (credentials: AuthCredential[]) => {
    return [...credentials].sort((a, b) => {
      const { key, order } = sortConfig.value;

      const compareSpecialKeys = () => {
        switch (key) {
          case 'count.asset_count':
            return (a.count?.asset_count ?? 0) - (b.count?.asset_count ?? 0);

          case 'count.derived_asset_count':
            return (a.count?.derived_asset_count ?? 0) - (b.count?.derived_asset_count ?? 0);

          case 'count.ppk_count':
            return (a.count?.ppk_count ?? 0) - (b.count?.ppk_count ?? 0);

          default:
            return compareStandardKeys(a, b, key);
        }
      };
      const compareStandardKeys = (a: AuthCredential, b: AuthCredential, key: keyof AuthCredential) => {
        const valA = a[key];
        const valB = b[key];

        if (typeof valA === 'string' && typeof valB === 'string') {
          return valA.localeCompare(valB);
        }

        if (typeof valA === 'number' && typeof valB === 'number') {
          return valA - valB;
        }

        return 0;
      };

      const result = compareSpecialKeys();
      return order === 'asc' ? result : -result;
    });
  };

  const updateSort = (
    key: SortKey,
    order: 'asc' | 'desc'
  ) => {
    sortConfig.value = { key, order };
    updateDisplayedCredentials();
  };

  const updateDisplayedCredentials = () => {
    const sortedCredentials = sortCredentials(allCredentials.value);

    const start = (currentPage.value - 1) * itemsPerPage.value;
    displayedCredentials.value = sortedCredentials.slice(
      start,
      start + itemsPerPage.value
    );
  };

  const updatePagination = (page: number, newItemsPerPage: number) => {
    currentPage.value = page;
    itemsPerPage.value = newItemsPerPage;
    updateDisplayedCredentials();
  };

  const updateName = (uuid: string, name: string) => {
    const credential = allCredentials.value.find(credential => credential.uuid === uuid);
    if (credential) {
      credential.name = name;
    } else {
      console.warn(`Credential with uuid ${uuid} not found.`)
    }
    updateDisplayedCredentials();
  };

  const resetCredentials = () => {
    allCredentials.value = [];
    displayedCredentials.value = [];
    authCredentialUUIDs.value = [];
  };

  const resetStore = () => {
    allCredentials.value = [];
    displayedCredentials.value = [];
    sortKey.value = "created";
    sortOrder.value = "asc";
    currentPage.value = 1;
    itemsPerPage.value = 10;
  };

  return {
    allCredentials,
    displayedCredentials,
    authCredentialUUIDs,
    totalCredentials,
    sortKey,
    sortOrder,
    currentPage,
    itemsPerPage,
    setCredentialUUIDs,
    fetchAllCredentials,
    updateDisplayedCredentials,
    updateSort,
    updatePagination,
    updateName,
    resetCredentials,
    resetStore,
  };

}, { persist: true })
